// 스테이지 진행(D3a, CONTENT-90D PRD 2.1): 지역 n의 n-1 ~ n-10을 차례로 연다. n-5 중간보스, n-10 지역보스, n-10 첫 클리어 → 다음 마을 → (n+1)-1.
// 순수 데이터·규칙(DOM·저장소 없음). 저장은 game/progress.ts(dungeonhero.stage.v1), 흐름 연결은 game/flow.ts.
//
// 한 스테이지 = 그 지역 지도 한 판(전투방 3 + 마지막 아레나). 지도 배치(levels.ts R1~R4)는 지역마다 하나이고, 스테이지가 판의 내용을 정한다:
//  - 전투방 웨이브: 지도에 적힌 웨이브를 스테이지 계단으로 키운다(마릿수 ×(0.8 + 0.05×(s−1)), 엘리트 +⌊(s−1)/3⌋).
//  - 마지막 아레나: 일반 스테이지는 정예 수비대(전투방), n-5는 중간보스, n-10은 지역보스.
//  - 몹 HP = 지역 배율(world.REGION_HP_MULT) × 스테이지 배율(1 + 0.12×(s−1)), 받는 피해도 같은 스테이지 배율(PRD 2.1).
//  - 보스 HP = 그 스테이지 일반 몹 평균 HP × BOSS_HP_RATIO(중간 40, 지역 70) — D0a 피해 시뮬(tools/sim/combat.ts)과 같은 식.
import { MOB_STATS } from '../config';
import { MAPS, REGION_IDS, regionNoOf, type BossKey, type MapDef, type RegionId, type WaveDef } from '../levels';
import { REGIONS } from '../regions';
import { RAID_MAPS, stageMapOf } from '../stageMaps';
import { BOSS_DEFS, REGION_BOSSES } from './bosses';
import { BOSS_HP_RATIO, DIFFICULTY, FINAL_BOSS_STAGE, MID_BOSS_STAGE, REGIONS_90D, REGION_HP_MULT, STAGES_PER_REGION, STAGE_STEP, mobLevel } from './world';
import { DIFFICULTY_LOOT } from './economy';
import { raidTuning, type RaidDiff, type RaidId } from './raid';
import type { AnyStageId, Difficulty, DropTableId, HardStageId, HellStageId, RegionNo, StageId } from './types';

/** 지금 플레이할 수 있는 지역 수(지도가 있는 지역, C2부터 6) */
export const PLAYABLE_REGIONS = REGION_IDS.length;
/** 플레이할 수 있는 마지막 스테이지 서수(1-1 = 1 … 6-10 = 60) */
export const LAST_STAGE_ORDINAL = PLAYABLE_REGIONS * STAGES_PER_REGION;

export function stageIdOf(region: number, index: number): StageId {
  return `r${region}-s${String(index).padStart(2, '0')}` as StageId;
}
/** 스테이지 id → 지역 번호·스테이지 번호. 형식이 아니거나 플레이할 수 없는 지역이면 null */
export function parseStage(id: unknown): { region: number; index: number } | null {
  if (typeof id !== 'string') return null;
  const m = /^r(\d)-s(\d\d)$/.exec(id);
  if (!m) return null;
  const region = Number(m[1]), index = Number(m[2]);
  if (region < 1 || region > PLAYABLE_REGIONS || index < 1 || index > STAGES_PER_REGION) return null;
  return { region, index };
}
/** 1-1 = 1 … 4-10 = 40. 잘못된 id는 0 */
export function stageOrdinal(id: unknown): number {
  const p = parseStage(id);
  return p ? (p.region - 1) * STAGES_PER_REGION + p.index : 0;
}
/** 서수 → 스테이지 id(1..LAST_STAGE_ORDINAL 밖이면 null) */
export function stageAt(ordinal: number): StageId | null {
  if (!Number.isInteger(ordinal) || ordinal < 1 || ordinal > LAST_STAGE_ORDINAL) return null;
  return stageIdOf(Math.floor((ordinal - 1) / STAGES_PER_REGION) + 1, ((ordinal - 1) % STAGES_PER_REGION) + 1);
}
/** 다음 스테이지(마지막이면 null). 하드 id면 다음 하드 스테이지 */
export function nextStageOf(id: AnyStageId): AnyStageId | null {
  if (parseHardStage(id)) return hardStageAt(hardOrdinal(id) + 1);
  if (parseHellStage(id)) return hellStageAt(hellOrdinal(id) + 1);
  return stageAt(stageOrdinal(id) + 1);
}
/** 화면 표기 '3-5'(하드는 '하드 3-5') */
export function stageLabel(id: AnyStageId): string {
  const h = parseHardStage(id);
  if (h) return `하드 ${h.region}-${h.index}`;
  const x = parseHellStage(id);
  if (x) return `헬 ${x.region}-${x.index}`;
  const p = parseStage(id);
  return p ? `${p.region}-${p.index}` : '';
}

// ── 하드 난이도(D3b, PRD 2.1 배율 · 0절 Day-0 '지역1~4 노말+하드') ──────────────
// 하드 스테이지 id = 노말 id의 r 자리를 h로(h3-s05 = 하드 3-5). 노말 진행·저장(stage.v1)은 이 id를 모른다(parseStage가 거른다).
// 해금: 하드 n-1 ~ n-10도 차례로 열리고(하드 진행 = 차례로 깬 하드 스테이지 수), 하드 지역 n은 노말 n-10을 깬 뒤에 열린다 —
// 노말로 그 지역 지역보스를 넘어선 플레이어만 같은 지도의 강화판에 선다(PRD 7절: 하드 1~2는 노말 지역4 뒤 4~5주 콘텐츠).
export function hardStageIdOf(region: number, index: number): HardStageId {
  return `h${region}-s${String(index).padStart(2, '0')}` as HardStageId;
}
/** 하드 스테이지 id → 지역·번호(형식이 아니거나 플레이할 수 없는 지역이면 null) */
export function parseHardStage(id: unknown): { region: number; index: number } | null {
  if (typeof id !== 'string' || id.charCodeAt(0) !== 104 /* h */) return null;
  return parseStage('r' + id.slice(1));
}
/** 노말·하드 어느 쪽이든: 지역·번호·난이도 */
export function parseAnyStage(id: unknown): { region: number; index: number; diff: Difficulty } | null {
  const h = parseHardStage(id);
  if (h) return { ...h, diff: 'hard' };
  const x = parseHellStage(id);
  if (x) return { ...x, diff: 'hell' };
  const p = parseStage(id);
  return p ? { ...p, diff: 'normal' } : null;
}
/** 하드 id → 같은 자리 노말 id(드롭 표·지도·보스는 노말 스테이지와 같다). 노말 id는 그대로, 잘못된 id는 null */
export function baseStageOf(id: unknown): StageId | null {
  const p = parseAnyStage(id);
  return p ? stageIdOf(p.region, p.index) : null;
}
/** 하드 서수(하드 1-1 = 1 … 하드 4-10 = 40). 하드 id가 아니면 0 */
export function hardOrdinal(id: unknown): number {
  const p = parseHardStage(id);
  return p ? (p.region - 1) * STAGES_PER_REGION + p.index : 0;
}
export function hardStageAt(ordinal: number): HardStageId | null {
  const n = stageAt(ordinal);
  return n ? (('h' + n.slice(1)) as HardStageId) : null;
}
/** 하드 지역이 열렸는지: 노말 그 지역 n-10을 깼고(노말 진행 ≥ n×10) 하드 앞 지역을 다 깼다(하드 진행 ≥ (n−1)×10) */
export function hardRegionUnlocked(normalCleared: number, hardCleared: number, region: number): boolean {
  return region >= 1 && region <= PLAYABLE_REGIONS && normalCleared >= region * STAGES_PER_REGION && hardCleared >= (region - 1) * STAGES_PER_REGION;
}
/** 하드 스테이지가 열렸는지(차례 + 노말 지역 클리어) */
export function isHardUnlocked(normalCleared: number, hardCleared: number, id: unknown): boolean {
  const p = parseHardStage(id);
  if (!p || !hardRegionUnlocked(normalCleared, hardCleared, p.region)) return false;
  return hardOrdinal(id) <= hardCleared + 1;
}
/** 하드 지역에서 지금 고를 수 있는 마지막 번호(잠겼으면 0) */
export function hardRegionFrontierIndex(normalCleared: number, hardCleared: number, region: number): number {
  if (!hardRegionUnlocked(normalCleared, hardCleared, region)) return 0;
  return Math.min(STAGES_PER_REGION, hardCleared + 1 - (region - 1) * STAGES_PER_REGION);
}
// ── 헬 난이도(C10, PRD 2.1 배율 · PROGRESSION-720H: 마지막 지역 노말 클리어 뒤 '헬 해금') ──────────────
// 헬 스테이지 id = 노말 id의 r 자리를 x로(x3-s05 = 헬 3-5). 노말·하드 진행·저장은 이 id를 모른다(parseStage·parseHardStage가 거른다).
// 해금(시뮬 tools/sim/pacing720.ts clearable과 같은 규칙): 헬은 마지막 지역 노말 n-10(= 노말 전부)을 깬 뒤에 열리고,
// 헬 지역 n은 하드 n-10을 깼고 헬 앞 지역을 다 깼을 때 열린다. 지역 안은 차례.
export function hellStageIdOf(region: number, index: number): HellStageId {
  return `x${region}-s${String(index).padStart(2, '0')}` as HellStageId;
}
/** 헬 스테이지 id → 지역·번호(형식이 아니거나 플레이할 수 없는 지역이면 null) */
export function parseHellStage(id: unknown): { region: number; index: number } | null {
  if (typeof id !== 'string' || id.charCodeAt(0) !== 120 /* x */) return null;
  return parseStage('r' + id.slice(1));
}
/** 헬 서수(헬 1-1 = 1 … 헬 6-10 = 60). 헬 id가 아니면 0 */
export function hellOrdinal(id: unknown): number {
  const p = parseHellStage(id);
  return p ? (p.region - 1) * STAGES_PER_REGION + p.index : 0;
}
export function hellStageAt(ordinal: number): HellStageId | null {
  const n = stageAt(ordinal);
  return n ? (('x' + n.slice(1)) as HellStageId) : null;
}
/** 헬 난이도가 열렸는지(노말 마지막 지역까지 전부 클리어) */
export function hellOpen(normalCleared: number): boolean {
  return normalCleared >= LAST_STAGE_ORDINAL;
}
/** 헬 지역 n이 열렸는지: 노말 전부 + 하드 n-10 클리어(하드 진행 ≥ n×10) + 헬 앞 지역 완료(헬 진행 ≥ (n−1)×10) */
export function hellRegionUnlocked(normalCleared: number, hardCleared: number, hellCleared: number, region: number): boolean {
  return region >= 1 && region <= PLAYABLE_REGIONS && hellOpen(normalCleared) && hardCleared >= region * STAGES_PER_REGION && hellCleared >= (region - 1) * STAGES_PER_REGION;
}
export function isHellUnlocked(normalCleared: number, hardCleared: number, hellCleared: number, id: unknown): boolean {
  const p = parseHellStage(id);
  if (!p || !hellRegionUnlocked(normalCleared, hardCleared, hellCleared, p.region)) return false;
  return hellOrdinal(id) <= hellCleared + 1;
}
export function hellRegionFrontierIndex(normalCleared: number, hardCleared: number, hellCleared: number, region: number): number {
  if (!hellRegionUnlocked(normalCleared, hardCleared, hellCleared, region)) return 0;
  return Math.min(STAGES_PER_REGION, hellCleared + 1 - (region - 1) * STAGES_PER_REGION);
}
export function stageKind(index: number): 'normal' | 'mid' | 'final' {
  return index === FINAL_BOSS_STAGE ? 'final' : index === MID_BOSS_STAGE ? 'mid' : 'normal';
}

// ── 해금 규칙(순차) ───────────────────────────────────
/**
 * 진행 = 차례로 깬 스테이지 수(cleared, 0..LAST). 다음에 열린 스테이지 = cleared + 1.
 * 스테이지 s(서수)가 열렸는지: s ≤ cleared + 1. 재도전은 자유(깬 스테이지는 언제든 다시).
 */
export function isUnlocked(cleared: number, id: StageId): boolean {
  const o = stageOrdinal(id);
  return o >= 1 && o <= Math.min(LAST_STAGE_ORDINAL, cleared + 1);
}
/** 지금 도전할 스테이지(아직 안 깬 첫 스테이지, 다 깼으면 마지막) */
export function frontierStage(cleared: number): StageId {
  return stageAt(Math.max(1, Math.min(LAST_STAGE_ORDINAL, cleared + 1)))!;
}
/** 지역이 열렸는지(그 지역 1스테이지가 열림) */
export function regionUnlocked(cleared: number, region: number): boolean {
  return region >= 1 && region <= PLAYABLE_REGIONS && cleared >= (region - 1) * STAGES_PER_REGION;
}
/** 그 지역에서 지금 고를 수 있는 마지막 스테이지 번호(지역이 잠겼으면 0) */
export function regionFrontierIndex(cleared: number, region: number): number {
  if (!regionUnlocked(cleared, region)) return 0;
  return Math.min(STAGES_PER_REGION, cleared + 1 - (region - 1) * STAGES_PER_REGION);
}
/** 지역보스(n-10)를 깬 지역 수 */
export function regionsCleared(cleared: number): number {
  return Math.floor(Math.max(0, Math.min(LAST_STAGE_ORDINAL, cleared)) / STAGES_PER_REGION);
}
/** 도착한 가장 높은 마을: 지역 n의 n-10을 처음 깨면 마을 n+1에 도착한다(world.ts nextTown, 지도가 있는 마을까지) */
export function highestTownOf(cleared: number, townCount: number): number {
  return Math.max(1, Math.min(townCount, 1 + regionsCleared(cleared)));
}
/**
 * 스테이지를 깼을 때 새 진행 값. 순서대로 다음 스테이지(cleared + 1)를 깼을 때만 오른다(이미 깬 스테이지 재도전은 그대로).
 * 결과: { cleared, first(이번이 첫 클리어), regionDone(이번 첫 클리어가 n-10) }
 */
export function applyClear(cleared: number, id: StageId): { cleared: number; first: boolean; regionDone: boolean } {
  const o = stageOrdinal(id);
  if (o !== cleared + 1) return { cleared, first: false, regionDone: false };
  return { cleared: o, first: true, regionDone: o % STAGES_PER_REGION === 0 };
}

// ── 판 계획 ─────────────────────────────────────────
export interface RoomPlan {
  readonly kind: 'battle' | 'boss';
  readonly waves: readonly WaveDef[];
  readonly boss?: BossKey;
}
export interface StagePlan {
  /** 스테이지 id(노말 r…·하드 h…). 자유 입장(지도 그대로, 진행 기록 없음)은 null */
  readonly stage: AnyStageId | null;
  /** 난이도(자유 입장·노말 = normal) */
  readonly diff: Difficulty;
  /** 지역 몹 레벨(world.mobLevel: 권장 구간 끝 + 하드 10) — 레벨 차 EXP 보정·방어 감소 기준 */
  readonly mobLevel: number;
  /** 처치 EXP 난이도 배율(시뮬 pacing과 같게 처치 EXP만 받는다 — 보스·클리어 EXP는 지역 고정값) */
  readonly xpDiff: number;
  /** 클리어 골드·카드 골드 난이도 배율(economy DIFFICULTY_LOOT.gold) */
  readonly goldDiff: number;
  readonly region: RegionId;
  /**
   * 이 판의 지도(S2·S6). 지역1·2 스테이지는 1·2 A, 3·4 A′, 5 M, 6·7 B, 8·9 B′, 10 F(stageMaps.ts stageMapOf), 그 밖의 지역·자유 입장은 지역 지도 그대로.
   * 지도 id는 항상 지역 id이고 판정 격자는 지도 객체별로 만든다(stageMaps.gridOfMap).
   */
  readonly map: MapDef;
  /** 지역 번호 1.. */
  readonly regionNo: number;
  /** 스테이지 번호 1..10(자유 입장 0) */
  readonly index: number;
  readonly kind: 'normal' | 'mid' | 'final' | 'free' | 'raid' | 'daily';
  /** 레이드 판(RAID-PRD): 레이드 id. 스테이지 진행·첫 클리어·카드 보상 대신 레이드 정산을 한다 */
  readonly raid?: RaidId;
  /** 레이드 난이도(C13, 없으면 노말) */
  readonly raidDiff?: RaidDiff;
  /** 일일 던전·무한의 탑 판(L9a, content/dailyPlans): 종류와 탑 층(보물고·광산 0). 스테이지 진행·카드 보상 대신 일일 장부 정산을 한다 */
  readonly daily?: { readonly kind: 'vault' | 'mine' | 'tower'; readonly floor: number };
  /** 방마다(지도 rooms 순서) 종류·웨이브·보스 */
  readonly rooms: readonly RoomPlan[];
  /** 일반 몹 HP·받는 피해 배율(지역 배율 × 스테이지 배율) */
  readonly mobHpMult: number;
  readonly mobDamageMult: number;
  /** 지역 위험(분출) 피해 배율 = 지역 × 스테이지(난이도 배율 없음 — 하드 ×2는 몹·보스만, PR #176 리뷰 P2) */
  readonly envDamageMult: number;
  /** 보스 최대 HP(보스 방이 없으면 0)와 원형 피해 배율(스테이지 배율 × 지역 피해 배율 × 보스 dmg) */
  readonly bossHp: number;
  readonly bossDamageMult: number;
  /** 보스 추가 패턴을 쓰는지(자유 입장 = 원형 패턴만, 기존 시험·하네스 그대로) */
  readonly bossExtras: boolean;
  /** 드롭 표(PRD 4.2: 스테이지 종류별) */
  readonly drops: DropTableId;
  /** 처치 EXP 스테이지 배율(SKILL-TREE 1절) */
  readonly xpStage: number;
}

/** 스테이지 배율(지역 안 HP·피해·처치 EXP): 1 + 0.12×(s−1), 소수 둘째 자리(world.ts StageDef.hpMul과 같다) */
export function stageMul(index: number): number {
  return Math.round((1 + STAGE_STEP * (Math.max(1, index) - 1)) * 100) / 100;
}
/** 그 지역 웨이브 구성 가중 평균 몹 HP(지역 배율 전) — 시뮬 tools/sim/combat.ts baseMobHp와 같은 식(world.ts 스테이지 웨이브 가안 기준) */
export function baseMobHp(regionNo: number): number {
  let hp = 0, n = 0;
  for (const st of REGIONS_90D[regionNo - 1].stages) for (const w of st.waves) for (let k = 0; k < 4; k++) { hp += w.counts[k] * MOB_STATS[k].hp; n += w.counts[k]; }
  return hp / n;
}
/** 보스 HP: 그 스테이지 일반 몹 평균 × BOSS_HP_RATIO(시뮬과 같은 식, 100 단위 반올림) */
export function stageBossHp(regionNo: number, index: number, tier: 'mid' | 'final'): number {
  const hp = baseMobHp(regionNo) * REGION_HP_MULT[regionNo - 1] * stageMul(index) * BOSS_HP_RATIO[tier];
  return Math.round(hp / 100) * 100;
}

/** 전투방 웨이브를 스테이지 계단으로 키운다. 0마리 칸은 0 그대로, 아니면 1마리 이상 */
function scaleWaves(waves: readonly WaveDef[], index: number): WaveDef[] {
  const k = 0.8 + 0.05 * (index - 1), add = Math.floor((index - 1) / 3);
  return waves.map((w) => {
    const c = w.counts.map((n) => (n > 0 ? Math.max(1, Math.round(n * k)) : 0));
    return { counts: [c[0], c[1], c[2], c[3] ?? 0] as const, elites: (w.elites ?? 0) + add };
  });
}
/** 일반 스테이지의 마지막 아레나: 정예 수비대(앞 방 마지막 웨이브를 바탕으로 엘리트를 더 세운다) */
function guardWaves(map: MapDef, index: number): WaveDef[] {
  const battles = map.rooms.filter((r) => r.kind === 'battle');
  const last = battles[battles.length - 1].waves;
  const base = scaleWaves([last[last.length - 1]], index)[0];
  const elites = 2 + Math.floor(index / 4);
  const c = base.counts;
  return [
    { counts: [Math.max(4, Math.round(c[0] * 0.6)), c[1], 0, c[3] ?? 0], elites: 1 },
    { counts: [Math.max(4, Math.round(c[0] * 0.5)), Math.max(1, c[1]), Math.max(1, c[2]), c[3] ?? 0], elites },
  ];
}

const planCache = new Map<string, StagePlan>();

/** 스테이지 판 계획(같은 id는 같은 객체를 돌려준다 — 판 시작에서만 부른다) */
export function stagePlan(id: StageId): StagePlan {
  const hit = planCache.get(id);
  if (hit) return hit;
  const p = parseStage(id);
  if (!p) throw new Error('알 수 없는 스테이지: ' + id);
  const region = REGION_IDS[p.region - 1];
  const map = stageMapOf(region, p.index) ?? MAPS[region];
  const kind = stageKind(p.index);
  const mul = stageMul(p.index);
  const info = REGIONS[region];
  const bossKey = kind === 'mid' ? REGION_BOSSES[p.region - 1].mid : kind === 'final' ? REGION_BOSSES[p.region - 1].final : undefined;
  const rooms: RoomPlan[] = map.rooms.map((r) => {
    if (r.kind === 'battle') return { kind: 'battle', waves: scaleWaves(r.waves, p.index) };
    return bossKey ? { kind: 'boss', waves: [], boss: bossKey } : { kind: 'battle', waves: guardWaves(map, p.index) };
  });
  const plan: StagePlan = {
    stage: id, region, map, regionNo: p.region, index: p.index, kind, rooms,
    mobHpMult: Math.round(REGION_HP_MULT[p.region - 1] * mul * 1000) / 1000,
    mobDamageMult: Math.round(info.mobDamageMult * mul * 1000) / 1000,
    envDamageMult: Math.round(info.mobDamageMult * mul * 1000) / 1000,
    bossHp: bossKey ? stageBossHp(p.region, p.index, kind === 'mid' ? 'mid' : 'final') : 0,
    bossDamageMult: bossKey ? Math.round(info.mobDamageMult * mul * BOSS_DEFS[bossKey].dmg * 1000) / 1000 : 1,
    bossExtras: true,
    drops: `drop_${kind}` as DropTableId,
    xpStage: mul,
    diff: 'normal', mobLevel: mobLevel(p.region as RegionNo, 'normal'), xpDiff: 1, goldDiff: 1,
  };
  planCache.set(id, plan);
  return plan;
}

/**
 * 하드 판 계획(D3b): 같은 자리 노말 계획(지도·방·웨이브·보스·드롭 표)에 PRD 2.1 하드 배율을 곱한다 —
 * 몹·보스 HP ×3, 받는 피해 ×2(일반 몹·보스 모두), 처치 EXP ×2.3, 몹 레벨 +10(레벨 차 보정·방어 감소), 골드 ×3.
 * 강화석 ×2·아이템 레벨 +10·보석 등급 +1은 드롭 문맥(game/loot.ts, plan.diff)이 economy·consumables 표로 붙인다. 마릿수·패턴은 노말과 같다.
 */
export function hardStagePlan(id: HardStageId): StagePlan {
  const hit = planCache.get(id);
  if (hit) return hit;
  const base = baseStageOf(id);
  if (!base || !parseHardStage(id)) throw new Error('알 수 없는 하드 스테이지: ' + id);
  const b = stagePlan(base), d = DIFFICULTY.hard;
  const plan: StagePlan = {
    ...b, stage: id, diff: 'hard',
    mobHpMult: Math.round(b.mobHpMult * d.hp * 1000) / 1000,
    mobDamageMult: Math.round(b.mobDamageMult * d.dmg * 1000) / 1000,
    bossHp: b.bossHp * d.hp,
    bossDamageMult: b.bossHp > 0 ? Math.round(b.bossDamageMult * d.dmg * 1000) / 1000 : b.bossDamageMult,
    mobLevel: mobLevel(b.regionNo as RegionNo, 'hard'), xpDiff: d.xp, goldDiff: DIFFICULTY_LOOT.hard.gold,
  };
  planCache.set(id, plan);
  return plan;
}

/**
 * 헬 판 계획(C10): 같은 자리 노말 계획에 PRD 2.1 헬 배율 — 몹·보스 HP ×6.5, 받는 피해 ×3.5, 처치 EXP ×5, 몹 레벨 +20, 골드 ×4.8.
 * 강화석·아이템 레벨 +20·보석 등급은 드롭 문맥(game/loot.ts, plan.diff = hell)이 economy 표로 붙인다.
 * 헬 보스는 '지옥불 각인'(content/hellAffix.ts)을 하나 더 쓴다 — 보스 패턴은 같고 각인이 판 위에 장판·폭발을 더한다.
 */
export function hellStagePlan(id: HellStageId): StagePlan {
  const hit = planCache.get(id);
  if (hit) return hit;
  const base = baseStageOf(id);
  if (!base || !parseHellStage(id)) throw new Error('알 수 없는 헬 스테이지: ' + id);
  const b = stagePlan(base), d = DIFFICULTY.hell;
  const plan: StagePlan = {
    ...b, stage: id, diff: 'hell',
    mobHpMult: Math.round(b.mobHpMult * d.hp * 1000) / 1000,
    mobDamageMult: Math.round(b.mobDamageMult * d.dmg * 1000) / 1000,
    bossHp: Math.round(b.bossHp * d.hp),
    bossDamageMult: b.bossHp > 0 ? Math.round(b.bossDamageMult * d.dmg * 1000) / 1000 : b.bossDamageMult,
    mobLevel: mobLevel(b.regionNo as RegionNo, 'hell'), xpDiff: d.xp, goldDiff: DIFFICULTY_LOOT.hell.gold,
  };
  planCache.set(id, plan);
  return plan;
}

/** 노말·하드·헬 어느 id든 판 계획 */
export function anyStagePlan(id: AnyStageId): StagePlan {
  if (parseHardStage(id)) return hardStagePlan(id as HardStageId);
  if (parseHellStage(id)) return hellStagePlan(id as HellStageId);
  return stagePlan(id as StageId);
}

/**
 * 자유 입장 계획(지도에 적힌 방·웨이브·보스 그대로, 지역 배율만): 스테이지를 고르지 않은 'enterDungeon' 명령(기존 시험·QA 하네스·?start=)용.
 * 진행(스테이지 해금)은 기록하지 않는다. 드롭 표는 game/loot.ts가 그 지역의 지금 진행 스테이지로 정한다.
 */
export function freePlan(region: RegionId): StagePlan {
  const key = 'free:' + region;
  const hit = planCache.get(key);
  if (hit) return hit;
  const map = MAPS[region];
  const info = REGIONS[region];
  const boss = map.rooms.find((r) => r.kind === 'boss');
  const plan: StagePlan = {
    stage: null, region, map, regionNo: regionNoOf(region), index: 0, kind: 'free',
    rooms: map.rooms.map((r) => ({ kind: r.kind, waves: r.waves, boss: r.kind === 'boss' ? (r.boss ?? 'treant') : undefined })),
    mobHpMult: info.mobHpMult, mobDamageMult: info.mobDamageMult, envDamageMult: info.mobDamageMult,
    // 원형 보스(고목 수호자·잿불 대주술사)는 config 수치 그대로(0 = 원형 HP). R3~R6 지역보스는 그 지역 10스테이지 값
    bossHp: boss && boss.boss !== undefined && boss.boss !== 'treant' && boss.boss !== 'ember' ? stageBossHp(regionNoOf(region), FINAL_BOSS_STAGE, 'final') : 0,
    bossDamageMult: boss && boss.boss !== undefined && boss.boss !== 'treant' && boss.boss !== 'ember' ? info.mobDamageMult : 1,
    bossExtras: false,
    drops: 'drop_normal',
    xpStage: 1,
    diff: 'normal', mobLevel: mobLevel(regionNoOf(region) as RegionNo, 'normal'), xpDiff: 1, goldDiff: 1,
  };
  planCache.set(key, plan);
  return plan;
}

/**
 * 레이드 판 계획(RAID-PRD): 무대(보스방 하나), 비룡 HP, 지역2 몹 레벨·처치 EXP. 스테이지 진행·첫 클리어·카드 보상은 없다(레이드 정산).
 * 비룡 행동·피해는 game/raid.ts가 content/raid WYVERN으로 정한다(bossDamageMult는 쓰지 않는다)
 */
export function raidPlan(id: RaidId, diff: RaidDiff = 'normal'): StagePlan {
  const key = 'raid:' + id + ':' + diff;
  const hit = planCache.get(key);
  if (hit) return hit;
  const map = RAID_MAPS[id];
  const info = REGIONS.r2;
  const plan: StagePlan = {
    stage: null, raid: id, raidDiff: diff, region: 'r2', map, regionNo: 2, index: 0, kind: 'raid',
    rooms: map.rooms.map(() => ({ kind: 'boss', waves: [], boss: 'ember' })),
    mobHpMult: info.mobHpMult, mobDamageMult: info.mobDamageMult, envDamageMult: info.mobDamageMult,
    bossHp: raidTuning(id, diff).hp, bossDamageMult: 1, bossExtras: false,
    drops: 'drop_final', xpStage: 1,
    diff: 'normal', mobLevel: mobLevel(2, 'normal'), xpDiff: 1, goldDiff: 1,
  };
  planCache.set(key, plan);
  return plan;
}
